import {
  useEffectWithController,
  useLoadableState,
  usePromise
} from '@carvana/merch-react-hooks';
import { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import DeviceContext, { useIsMobile } from '~/context/common/DeviceContext';
import TradeContext from '~/context/common/TradeContext';
import { RecommendedVehicle } from '~/contracts/vdp';
import { getBulkAvailabilityInfo } from '~/hooks/vdp/services';
import {
  getAssociatedVdpEventId,
  getFavoriteVehicleIds,
  getGeoLocation,
  getSearchRequestId,
  getUser,
  getVehicleDetails
} from '~/selectors';
import { RequestedFeature } from '~/services/common/contracts';
import { fetchRecommendations } from '~/services/vdp';
import { BrowserCookies } from '~/utilities';
import generateErrorLogger from '~/utilities/errorHandlers';

function useGetRecommendations(
  numVehiclesToReturn = 6,
  filterByMakeModel = false
) {
  const { data, error, isLoaded, setData, setError, setLoading } =
    useLoadableState<RecommendedVehicle[]>([]);
  const [localVehicles, setLocalVehicles] = useState<RecommendedVehicle[]>([]);
  const mounted = usePromise();
  const associatedVdpEventId = useSelector(getAssociatedVdpEventId);
  const favorites = useSelector(getFavoriteVehicleIds);
  const location = useSelector(getGeoLocation);
  const searchRequestId = useSelector(getSearchRequestId); // maps browser's events to search API's events
  const isMobile = useIsMobile();
  const { browserName } = useContext(DeviceContext);
  const { isTradeInApplied } = useContext(TradeContext);

  const {
    isLoaded: isUserLoaded,
    isLoggedIn = false,
    referrer = '',
    userId = ''
  } = useSelector(getUser);
  const {
    make = '',
    model = '',
    vehicleId = 0
  } = useSelector(getVehicleDetails);

  useEffectWithController(
    controller => {
      if (!isUserLoaded) return;
      const requestedFeaturesToUse = new Array<RequestedFeature>()
        .concat(['LoanTermPricing', 'EarliestAcquisitionBoosting'])
        .concat(
          isTradeInApplied && isLoggedIn
            ? ['ApplyTradeIn', 'TermsWithTradeInEquity']
            : []
        );

      async function fetchVehicles() {
        setLoading();
        let vehicles = await fetchRecommendations(
          {
            analyticsData: {
              searchRequestId,
              isMobileDevice: isMobile,
              browser: browserName,
              filterSource: 'notSpecified',
              referrer
            },
            browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
            filters: filterByMakeModel
              ? {
                  makes: [
                    {
                      name: make,
                      models: [
                        {
                          name: model
                        }
                      ]
                    }
                  ]
                }
              : {},
            numVehiclesToReturn,
            requestedFeatures: requestedFeaturesToUse,
            vehicleId,
            zip5: location.zip
          },
          isLoggedIn,
          controller
        );

        // if no vehicles are returned, try again without make/model filters
        if (vehicles.length < 6 && filterByMakeModel) {
          vehicles = await fetchRecommendations(
            {
              analyticsData: {
                searchRequestId,
                isMobileDevice: isMobile,
                browser: browserName,
                filterSource: 'notSpecified',
                referrer
              },
              browserCookieId: BrowserCookies.get('BrowserCookieId') ?? '',
              filters: {},
              numVehiclesToReturn,
              requestedFeatures: requestedFeaturesToUse,
              vehicleId,
              zip5: location.zip
            },
            isLoggedIn,
            controller
          );
        }

        if (vehicles.length === 0) return [];

        const deliveryData = await getBulkAvailabilityInfo(
          vehicles,
          location.zip,
          associatedVdpEventId,
          userId
        );

        return vehicles.map(vehicle => ({
          ...vehicle,
          ...deliveryData.find(
            vehicleData => vehicleData.vehicleId === vehicle.vehicleId
          )
        }));
      }

      mounted(fetchVehicles())
        .then(vehicles => {
          setLocalVehicles(vehicles);
          if (vehicles.length === 0) setData([]);
        })
        .catch(
          generateErrorLogger(
            'Get recommended vehicles',
            {},
            { callback: setError }
          )
        );
    },
    [
      associatedVdpEventId,
      browserName,
      filterByMakeModel,
      isLoggedIn,
      isMobile,
      isTradeInApplied,
      isUserLoaded,
      location,
      make,
      model,
      mounted,
      numVehiclesToReturn,
      referrer,
      searchRequestId,
      setData,
      setError,
      setLoading,
      userId,
      vehicleId
    ]
  );

  useEffect(() => {
    if (localVehicles.length === 0) return;

    setData(
      localVehicles.map<RecommendedVehicle>(vehicle => ({
        ...vehicle,
        isFavorited: favorites.includes(vehicle.vehicleId)
      }))
    );
  }, [favorites, localVehicles, setData]);

  return {
    error,
    isLoaded,
    vehicles: data
  };
}

export default useGetRecommendations;
